import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/tpa-settings/react';
import { useSidebarLayoutBase } from '../../contexts/SidebarLayoutBase/SidebarLayoutBaseContext';
import { useChatAPI } from '../../../../../../../hooks/useChatAPI';
import {
  Button,
  ButtonPriority,
  ButtonSize,
  HoverStyles,
  SkeletonLine,
  Text,
  TextField,
  TextFieldTheme,
  TextPriority,
  TextTypography,
  IconButton,
} from 'wix-ui-tpa/cssVars';
// import AiChatControls from '../AiChatEasterEgg/AiChatControls';
import {
  ChatMessage,
  Role,
} from '@wix/ambassador-legends-persona-v1-chat/types';
import { SendFill } from '@wix/wix-ui-icons-common/on-stage';
import { Dismiss, SparklesFilledSmall } from '@wix/wix-ui-icons-common';
import { Challenges } from '../../../../../../../editor/types/Experiments';
import challengeSettings from '../../../../../settingsParams';
import { st, classes } from './AIChat.st.css';

export default () => {
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const { isMobile } = useEnvironment();
  const settings = useSettings();
  const { isAiChatEnabled, toggleAiChatVisibility } = useSidebarLayoutBase();
  const { newConversation, isPending, messages, sendMessage, personaSettings } =
    useChatAPI();
  const [newMessage, setNewMessage] = useState<string>('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const title = settings.get(challengeSettings.aiTitleTxt);

  const isAiChatEasterEggEnabled = experiments.enabled(
    Challenges.aiChatEasterEgg,
  );
  const showNewConversationButton = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('withNewChatButton');
  }, []);

  const placeholder =
    (personaSettings?.enablePlaceholder && personaSettings?.placeholder) ||
    t('aiHelper.communicationRules.placeholder.defaultValue', {
      defaultValue: 'Ask me anything about the course ...',
    });

  useEffect(() => {
    if (isPending) {
      return;
    }

    if (bottomRef?.current) {
      bottomRef.current.scrollIntoView?.({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [messages, isPending]);

  if (!isAiChatEnabled) {
    return null;
  }

  return (
    <>
      <div className={st(classes.header, { mobile: isMobile })}>
        <div className={classes.headerLeft}>
          <Text
            tagName="h3"
            typography={TextTypography.runningText}
            priority={TextPriority.primary}
          >
            {title as string}
          </Text>

          {/* isAiChatEasterEggEnabled ? <AiChatControls /> : null */}
        </div>

        <IconButton
          onClick={toggleAiChatVisibility}
          aria-label={t('live-site.ai-chat.close.aria-label')}
          icon={<Dismiss />}
        />
      </div>

      <div className={classes.inner}>
        {isPending ? (
          <ul className={classes.skeleton}>
            <SkeletonLine />
            <SkeletonLine />
            <SkeletonLine />
            <SkeletonLine />
            <SkeletonLine />
          </ul>
        ) : (
          <>
            <ul className={classes.messagesList}>
              {(messages || [])
                .toReversed()
                .map((message: ChatMessage, index: number) => {
                  return (
                    <li
                      key={message.id}
                      className={`${classes.message} ${
                        message.role === Role.ASSISTANT
                          ? classes.assistantMessage
                          : classes.userMessage
                      }`}
                    >
                      {message.role === Role.ASSISTANT ? (
                        <SparklesFilledSmall />
                      ) : null}
                      <Text priority={TextPriority.primary}>
                        {message.id !== '-1' ? message.content : '...'}
                      </Text>
                    </li>
                  );
                })}
            </ul>
            <form
              className={classes.messageForm}
              onSubmit={(e) => {
                void sendMessage(newMessage);
                setNewMessage('');

                e.preventDefault();
              }}
            >
              <TextField
                className={classes.textField}
                theme={TextFieldTheme.Box}
                placeholder={placeholder}
                suffix={
                  <div
                    onClick={() => {
                      void sendMessage(newMessage);
                      setNewMessage('');
                    }}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <SendFill />
                  </div>
                }
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                }}
              />
              {showNewConversationButton ? (
                <Button
                  priority={ButtonPriority.basicSecondary}
                  size={ButtonSize.tiny}
                  hoverStyle={HoverStyles.underline}
                  onClick={() => newConversation()}
                  fullWidth={true}
                  style={{ marginTop: '15px', borderRadius: '20px' }}
                >
                  Start New Conversation
                </Button>
              ) : null}
            </form>

            <div
              ref={bottomRef}
              style={{ position: 'relative', top: '20px' }}
            />
          </>
        )}
      </div>
    </>
  );
};
